/**
 * Shared Types and Utilities for Offline Data Hooks
 * 
 * Common interfaces, types, and utility functions used across all offline data hooks.
 * This file contains the shared contracts that ensure consistency across the refactored hook files.
 */

import type { TechniqueOffline } from '@/services/offline/indexeddb/schema';
import type { Technique } from '@/types/technique';

/**
 * Data fetch options for offline hooks
 */
export interface DataFetchOptions {
  cacheFirst?: boolean;
  forceRefresh?: boolean;
  includeOfflineChanges?: boolean;
  maxAge?: number; // in milliseconds
}

/**
 * Standard data fetch result interface used by all offline hooks
 */
export interface DataFetchResult<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  isFromCache: boolean;
  lastUpdated: string | null;
  refresh: () => Promise<void>;
}

/**
 * Transform TechniqueOffline to Technique for UI components
 * 
 * Converts the IndexedDB storage format to the UI-expected format,
 * specifically handling boolean conversions from SQLite number format.
 */
export function transformTechniqueOfflineToTechnique(offline: TechniqueOffline): Technique {
  return {
    ...offline,
    is_user_created: Boolean(offline.is_user_created), // Convert from number to boolean
    is_private: Boolean(offline.is_private), // Convert from number to boolean
  };
}
